import { Card } from "@tokenterminal/ui/Card"
import { getMart, getMarts } from "api/ApiCalls"
import { PageMeta } from "components/PageMeta"
import { Tracking } from "components/Tracking"
import { shouldSkipStaticBuild } from "helpers/config"
import { V3Layout } from "layouts/v3"
import { type PageType } from "modules/analytics/types"
import { DataGridProvider } from "modules/datatable/DataGridContext"
import { DataGridWithPresetQuery } from "modules/datatable/WithPresetQuery"
import { MartDataGrid } from "modules/MartDataGrid"
import { MartDataGridHeader } from "modules/MartDataGrid/MartDataGridHeader"
import { DatasetsAside } from "modules/v3/DatasetsAside"
import { type GetStaticPaths, type GetStaticProps } from "next"
import { useEffect, useRef, useState, Suspense } from "react"
import { type Mart } from "types/ApiTypes"

interface PageProps {
  mart: Mart
}

const MartPageTemplate = ({ mart }: PageProps) => {
  const headerRef = useRef<HTMLDivElement>(null)
  const [headerHeight, setHeaderHeight] = useState(105)

  useEffect(() => {
    if (headerRef.current) {
      const height = Math.round(headerRef.current.offsetHeight)

      if (height !== headerHeight) {
        setHeaderHeight(height)
      }
    }
  }, [headerHeight])

  if (!mart) {
    return null
  }

  return (
    <Card
      flex="1 1 auto"
      height="100%"
      display="flex"
      flexDirection="column"
      gap="4x"
    >
      <MartDataGridHeader ref={headerRef} mart={mart} />
      <Suspense fallback={null}>
        <MartDataGrid
          martId={mart.mart_id}
          columns={mart.column_definitions}
          defaultPreset={mart.default_preset}
          component={DataGridWithPresetQuery}
        />
      </Suspense>
    </Card>
  )
}

const MartPage = ({ mart, ...rest }: PageProps) => {
  if (!mart) {
    return null
  }

  return (
    <Tracking pageId={mart.slug} pageType={"dataset" as PageType}>
      <PageMeta meta={{ description: mart.description, title: mart.name }} />
      <DatasetsAside isDefaultOpen={false} />
      <DataGridProvider id={mart.mart_id} key={mart.mart_id}>
        <MartPageTemplate mart={mart} {...rest} />
      </DataGridProvider>
    </Tracking>
  )
}

MartPage.getLayout = V3Layout

export default MartPage

export const getStaticPaths: GetStaticPaths = async () => {
  if (shouldSkipStaticBuild()) {
    return {
      paths: [],
      fallback: true, // always use fallback to allow page build on request
    }
  }

  const marts = await getMarts()

  return {
    paths: marts.map(({ slug }) => ({
      params: {
        martSlug: slug,
      },
    })),
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps<
  PageProps,
  { martSlug: string }
> = async ({ params }) => {
  if (typeof params?.martSlug !== "string") {
    return {
      notFound: true,
      props: {},
    }
  }

  const mart = await getMart(params.martSlug, true)

  if (!mart) {
    return {
      notFound: true,
      props: {},
    }
  }

  // Sort default preset as first. Presets are shown in this order in UI
  mart.presets.sort(({ is_default: a }, { is_default: b }) =>
    a ? -1 : b ? 1 : 0
  )

  // Deprecated logic, which used to get initialData for the static page. This created too large pages however so disabled. Logic works
  // const { default_preset: defaultPreset } = mart

  // const requestParams = DEFAULT_SSRM_REQUEST
  // if (defaultPreset) {
  //   if (defaultPreset.config.filters) {
  //     requestParams.filterModel = defaultPreset.config.filters
  //   }

  //   // Gather all sorted columns and pass to initial request
  //   const sortModel = defaultPreset.config.columns
  //     .filter(({ sort }) => sort)
  //     .map(({ colId, sort, sortIndex }) => {
  //       if (colId === "project_name") {
  //         return {
  //           isSortCaseInsensitive: true,
  //           colId,
  //           sort: sort!,
  //           sortIndex,
  //         }
  //       }
  //       return { colId, sort: sort!, sortIndex }
  //     })

  //   if (sortModel.length) {
  //     if (sortModel.some(({ sortIndex }) => typeof sortIndex === "number")) {
  //       sortModel.sort(({ sortIndex: a }, { sortIndex: b }) => a! - b!)
  //     }

  //     requestParams.sortModel = sortModel.map(
  //       ({ sortIndex, ...model }) => model
  //     )
  //   }

  //   // Gather all grouping columns and pass to initial request
  //   const groupCols = defaultPreset.config.columns
  //     .filter(({ rowGroup }) => rowGroup)
  //     // displayName can be anything
  //     .map(({ colId }) => ({ id: colId, displayName: "" }))

  //   if (groupCols.length) {
  //     requestParams.rowGroupCols = groupCols
  //   }

  //   // Gather all aggregate columns and pass to initial request
  //   const valueCols = defaultPreset.config.columns
  //     .filter(({ aggFunc }) => typeof aggFunc === "string")
  //     // displayName can be anything. field is a must have
  //     .map(({ colId, aggFunc }) => ({
  //       id: colId,
  //       field: colId,
  //       displayName: "",
  //       aggFunc: aggFunc as string,
  //     }))

  //   if (valueCols.length) {
  //     requestParams.valueCols = valueCols
  //   }
  // }

  return {
    props: {
      mart,
    },
  }
}
